// src/context/CartContext.js
// Guarantees Firestore order writes and enforces unique order numbers.
// - Reserves order IDs transactionally to avoid duplicates.
// - Writes/updates the Firestore order document BEFORE hitting external APIs,
//   then updates status after OrderDesk + stock updates.
// - If a user-provided PO collides with an existing order, we fall back to an auto ID.
//
// Note: No confirmation email is sent from here (as per your comment). Email is handled in Cart.js.

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { db, auth } from '../services/firebase';
import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    Timestamp,
    serverTimestamp,
    runTransaction,
    onSnapshot,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import sendOrderToOrderDesk from '../services/orderDeskAPI';
import { prepareOrderPayloadForOrderDesk } from '../utils/orderPlacementMarkers';
import { applyPrepaidMarkersToOrder } from '../utils/cartSubmitAndPay';
import { logAPICall, logError, captureFailedOrder } from '../services/telemetry';
import { getNetworkConditions } from '../services/apiInterceptor';
import { formatDDMMYYYY } from '../services/dateService';
import unifiedStockService, { SYNC_TARGET } from '../services/unifiedStockService.js';
import { MOVEMENT_TYPE, SOURCE_SYSTEM } from '../services/stockMovementService.js';
import { getCustomerStockConstraints, getRemainingAddable } from '../utils/customerProductStock.js';
import unifiedProductService from '../services/unifiedProductService.js';
import { extractAdditionalEmailRowsFromUserProfileData } from '../utils/accountAdditionalEmailsUtils';
import { applySearcysOrderFields } from '../utils/searcysOrderPeculiarity';
import { getOrderPersonNameFromUserProfile } from '../utils/orderPersonNameUtils';
import {
    calculateCartSubtotalGbp,
    calculateWholesaleShippingFromCart,
} from '../utils/wholesaleShippingUtils';
import {
    buildSavedAddressPayload,
    nextSavedAddressFieldName,
    resolveUserDocIdForSavedAddress,
} from '../utils/savedAddressUtils';
import { resolveUserPhone } from '../utils/userPhoneFields';
import {
    buildOrderDeskLineFromCartLine,
    buildCartPriceChangeNotice,
    filterActionablePriceCorrections,
    revalidateCartAgainstTier,
    resolveCartStampPricingKey,
} from '../utils/customerPricingContract';
import {
    normalizePricingKey,
    resolvePricingKeyFromUserData,
    coerceCustomerPricingKey,
} from '../services/pricingTierService';
import {
    filterOrderableCartItems,
    isCartEnquiryLine,
    partitionCartItems,
} from '../utils/cartEnquiryLines';

const STOCK_API_URL = 'https://stockOperations-844411985132.europe-north1.run.app';
const PRODUCTS_API_URL = process.env.REACT_APP_PRODUCTS_API_URL || '';

const CartContext = createContext();
/** Pricing tier only — browse pages subscribe here so add-to-cart does not remount product lists. */
const CustomerPricingKeyContext = createContext(null);

export function useCart() { return useContext(CartContext); }
export function useCustomerPricingKey() { return useContext(CustomerPricingKeyContext); }

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const validateAndNormalizeAddress = (address) => {
    if (!address) return null;
    const postCode = address.postalCode || address.postCode || '';
    const normalized = {
        line1: address.line1 ? address.line1.trim() : '',
        city: address.city ? address.city.trim() : '',
        postCode: postCode.trim(),
        line2: address.line2 ? address.line2.trim() : '',
        line3: address.addressLine3 ? address.addressLine3.trim() : '',
        companyName: address.companyName ? address.companyName.trim() : '',
    };
    if (!normalized.line1 || !normalized.city || !normalized.postCode) {
        return null;
    }
    return normalized;
};

const getUserName = (userData) => getOrderPersonNameFromUserProfile(userData, { fallbackUnknown: true });

function generateOrderNumber() {
    const now = new Date();
    const yy = now.getFullYear().toString().slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const rnd = String(Math.floor(Math.random() * 900) + 100);
    return `${yy}${mm}${dd}${rnd}`;
}

// Reserve a document ID in 'orders' collection using a transaction to avoid races.
// If doc exists, throws to signal collision.
async function reserveOrderId(orderId) {
    const ref = doc(db, 'orders', orderId);
    await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (snap.exists()) {
            throw new Error('ORDER_ID_EXISTS');
        }
        tx.set(ref, {
            reserved: true,
            status: 'Pending',
            createdAt: serverTimestamp(),
            lastUpdatedAt: serverTimestamp(),
        }, { merge: true });
    });
    console.log(`✅ [CartContext] Order ID ${orderId} successfully reserved`);
}

// Generate a unique order ID, trying a preferredId first (e.g., PO).
// Falls back to generated IDs, incrementing the random number on collision.
async function generateUniqueOrderId(preferredId = null, maxAttempts = 50) {
    // Try preferred first (if provided)
    if (preferredId && preferredId.trim() !== '') {
        try {
            await reserveOrderId(preferredId.trim());
            return preferredId.trim();
        } catch (e) {
            // Collision — fall through to auto generation
            console.warn('[CartContext] Preferred order ID already exists, generating a new one.');
        }
    }
    
    // Generate base order number (YYMMDD + random 3 digits)
    const now = new Date();
    const yy = now.getFullYear().toString().slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const datePrefix = `${yy}${mm}${dd}`;
    
    // Start with a random number and increment on collision
    let randomSuffix = Math.floor(Math.random() * 900) + 100; // 100-999
    
    for (let i = 0; i < maxAttempts; i++) {
        const candidate = `${datePrefix}${randomSuffix}`;
        try {
            await reserveOrderId(candidate);
            console.log(`✅ [CartContext] Generated unique order ID: ${candidate} (attempt ${i + 1})`);
            return candidate;
        } catch (e) {
            console.warn(`[CartContext] Order ID ${candidate} collision, incrementing... (attempt ${i + 1}/${maxAttempts})`);
            // Increment the random suffix, wrap around if needed
            randomSuffix++;
            if (randomSuffix > 999) {
                randomSuffix = 100; // Wrap back to start
            }
        }
    }
    
    // Final attempt with timestamp suffix if all increments failed
    const lastResort = `${datePrefix}${randomSuffix}-${Date.now().toString().slice(-4)}`;
    console.warn(`[CartContext] Max attempts reached, using timestamp-based ID: ${lastResort}`);
    await reserveOrderId(lastResort);
    return lastResort;
}

// Convert form/new-address shape into the shipping block that OrderDesk/Firestore expects
function toShippingBlockFromNormalized(normalized, userData, name) {
    return {
        first_name: name.first,
        last_name: name.last,
        company: normalized.companyName || userData.companyName || '',
        address1: normalized.line1,
        address2: normalized.line2,
        address3: normalized.line3,
        city: normalized.city,
        state: '',
        postal_code: normalized.postCode,
        phone: resolveUserPhone(userData),
    };
}

function toShippingBlockForCollection(userData, name) {
    const sa = userData.shippingAddress || {};
    return {
        first_name: name.first,
        last_name: name.last,
        company: sa.companyName || userData.companyName || '',
        address1: sa.line1 || '',
        address2: sa.line2 || '',
        address3: sa.line3 || '',
        city: sa.city || '',
        state: sa.state || '',
        postal_code: sa.postCode || sa.postalCode || '',
        phone: resolveUserPhone(userData),
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────
export function CartProvider({ children }) {
    const [cartItems, setCartItems] = useState(() => {
        const storedCart = localStorage.getItem('cartItems');
        return storedCart ? JSON.parse(storedCart) : [];
    });
    const [orderStatus, setOrderStatus] = useState('');
    const [orderNote, setOrderNote] = useState('');
    const [deliveryDate, setDeliveryDate] = useState(new Date());
    const [purchaseOrder, setPurchaseOrder] = useState('');
    const [addresses, setAddresses] = useState([]);

    const userDataCache = useRef({ data: null, timestamp: 0 });
    const addressesLastFetchedRef = useRef(0);
    const cartItemsRef = useRef(cartItems);
    const [customerPricingKey, setCustomerPricingKey] = useState(null);
    const [cartPriceNotice, setCartPriceNotice] = useState(null);

    useEffect(() => {
        cartItemsRef.current = cartItems;
    }, [cartItems]);

    const applyCartRevalidation = (result, prevItems) => {
        if (!result.ok) {
            setCartItems([]);
            setOrderStatus(result.message || 'Some items are not on your price list.');
            setCartPriceNotice(null);
            return result;
        }

        const needsUpdate =
            result.priceCorrections.length > 0 ||
            prevItems.some((i) => !i.pricingKey || i.pricingKey !== result.pricingKey);

        if (needsUpdate) {
            setCartItems(result.lines);
        }

        const actionable = filterActionablePriceCorrections(
            result.priceCorrections,
            prevItems,
            result.pricingKey
        );
        if (actionable.length > 0) {
            setCartPriceNotice(buildCartPriceChangeNotice(actionable, prevItems));
        } else {
            setCartPriceNotice(null);
        }

        return { ...result, priceCorrections: actionable };
    };

    const revalidateCartPrices = useCallback(async () => {
        const key = customerPricingKey;
        const prev = cartItemsRef.current;
        if (!key || !prev.length) {
            setCartPriceNotice(null);
            return { ok: true, priceCorrections: [], lines: prev, pricingKey: key, message: '' };
        }

        try {
            const result = await revalidateCartAgainstTier(prev, key);
            return applyCartRevalidation(result, prev);
        } catch (err) {
            console.warn('[CartContext] Cart revalidation failed:', err);
            return {
                ok: false,
                priceCorrections: [],
                lines: prev,
                message: 'Could not refresh prices on your cart.',
            };
        }
    }, [customerPricingKey]);

    const dismissCartPriceNotice = () => setCartPriceNotice(null);

    useEffect(() => {
        localStorage.setItem('cartItems', JSON.stringify(cartItems));
    }, [cartItems]);

    // Live tier from users/{uid}.Pricing (keeps browse + cart in sync)
    useEffect(() => {
        let unsubProfile = () => {};

        const unsubAuth = onAuthStateChanged(auth, (u) => {
            unsubProfile();
            unsubProfile = () => {};

            if (!u) {
                setCustomerPricingKey(null);
                return;
            }

            unsubProfile = onSnapshot(
                doc(db, 'users', u.uid),
                (snap) => {
                    const key = snap.exists()
                        ? resolvePricingKeyFromUserData(snap.data())
                        : normalizePricingKey(null);
                    setCustomerPricingKey(key);
                },
                (err) => {
                    console.warn('[CartContext] users/{uid} pricing listener failed:', err);
                    setCustomerPricingKey(normalizePricingKey(null));
                }
            );
        });

        return () => {
            unsubAuth();
            unsubProfile();
        };
    }, []);

    // Revalidate or clear cart when assigned tier changes
    useEffect(() => {
        if (!customerPricingKey) return;
        let cancelled = false;

        (async () => {
            const prev = cartItemsRef.current;
            if (!prev.length) return;

            const wrongTier = prev.some(
                (i) => i.pricingKey && i.pricingKey !== customerPricingKey
            );
            if (wrongTier) {
                if (!cancelled) {
                    setCartItems([]);
                    setOrderStatus('Your assigned price list changed; your cart was cleared.');
                }
                return;
            }

            try {
                const result = await revalidateCartAgainstTier(prev, customerPricingKey);
                if (cancelled) return;
                applyCartRevalidation(result, prev);
            } catch (err) {
                console.warn('[CartContext] Cart tier revalidation failed:', err);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [customerPricingKey]);

    // Admin-triggered cart clear (sales support)
    useEffect(() => {
        const unsubAuth = onAuthStateChanged(auth, (u) => {
            if (!u) return;
            const ref = doc(db, 'cart_admin_actions', u.uid);
            const unsubDoc = onSnapshot(ref, (snap) => {
                if (!snap.exists()) return;
                const data = snap.data() || {};
                const clearedAt = data.cartClearedAt;
                const clearedMs =
                    typeof clearedAt?.toMillis === 'function'
                        ? clearedAt.toMillis()
                        : (typeof clearedAt?.seconds === 'number' ? clearedAt.seconds * 1000 : null);
                if (!clearedMs) return;

                const key = 'cartAdminClearAtMs';
                const lastSeen = Number(localStorage.getItem(key) || 0);
                if (clearedMs <= lastSeen) return;

                // Quietly clear cart state + storage
                setCartItems([]);
                try {
                    localStorage.removeItem('cartItems');
                } catch { }
                localStorage.setItem(key, String(clearedMs));
            });

            // When user changes / signs out, clean up listener
            return () => unsubDoc();
        });

        return () => {
            try { unsubAuth(); } catch { }
        };
    }, []);

    const orderItems = cartItems.map((item, index) => ({
        id: index.toString(),
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        code: item.code,
        stock: item.stock,
        packSize: item.packSize,
    }));

    const stampProductForCart = (product) => {
        const stamped = resolveCartStampPricingKey(product, customerPricingKey);
        if (!stamped) {
            window.alert(
                customerPricingKey
                    ? 'This product could not be added. Please refresh the page and try again.'
                    : 'Your price list is still loading. Please try again in a moment.'
            );
        }
        return stamped;
    };

    const addToCart = (product) => {
        if (isCartEnquiryLine(product) || product?.enquiryOnly) return;
        const stamped = stampProductForCart(product);
        if (!stamped) return;

        setCartItems((prev) => {
            const idx = prev.findIndex((i) => i.code === stamped.code && !isCartEnquiryLine(i));
            const existingQty = idx >= 0 ? prev[idx].quantity : 0;
            const { isUnlimited } = getCustomerStockConstraints(stamped);
            const remaining = getRemainingAddable(stamped, existingQty);
            if (!isUnlimited && remaining < 1) {
                window.alert(`Max stock reached for ${stamped.name || stamped.code}.`);
                return prev;
            }
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = {
                    ...next[idx],
                    ...stamped,
                    quantity: existingQty + 1,
                    price: stamped.price != null ? stamped.price : next[idx].price,
                    stock: stamped.stock !== undefined ? stamped.stock : next[idx].stock,
                };
                return next;
            }
            return [...prev, { ...stamped, quantity: 1 }];
        });
    };

    const addItemsToCart = (products) => {
        setCartItems((prev) => {
            const updated = [...prev];
            products.forEach((prod) => {
                if (isCartEnquiryLine(prod) || prod?.enquiryOnly) return;
                const stamped = stampProductForCart(prod);
                if (!stamped) return;

                const addQty = Math.max(0, Number(stamped.quantity) || 0);
                if (addQty <= 0) return;

                const idx = updated.findIndex((i) => i.code === stamped.code && !isCartEnquiryLine(i));
                const existingQty = idx >= 0 ? updated[idx].quantity : 0;
                const { isUnlimited, maxOrderable } = getCustomerStockConstraints(stamped);

                let allowedDelta = addQty;
                if (!isUnlimited) {
                    const remaining = getRemainingAddable(stamped, existingQty);
                    allowedDelta = Math.min(addQty, remaining);
                    if (allowedDelta < addQty) {
                        const name = stamped.name || stamped.code || 'This product';
                        window.alert(
                            `Only ${remaining} more of "${name}" can be added (${maxOrderable} in stock).`,
                        );
                    }
                    if (allowedDelta <= 0) return;
                }

                if (idx >= 0) {
                    updated[idx] = {
                        ...updated[idx],
                        ...stamped,
                        quantity: existingQty + allowedDelta,
                        price: stamped.price != null ? stamped.price : updated[idx].price,
                        stock: stamped.stock !== undefined ? stamped.stock : updated[idx].stock,
                    };
                } else {
                    updated.push({ ...stamped, quantity: allowedDelta });
                }
            });
            return updated;
        });
    };

    const addEnquiryReminder = (line) => {
        if (!line || !isCartEnquiryLine(line) || !line.code) return;
        setCartItems((prev) => {
            const without = prev.filter(
                (i) => !(isCartEnquiryLine(i) && String(i.code) === String(line.code))
            );
            return [...without, { ...line, price: 0, quantity: 1, enquiryOnly: true }];
        });
    };

    const increaseQuantity = (product) => {
        if (isCartEnquiryLine(product)) return;
        setCartItems((prev) => {
            const updated = [...prev];
            const idx = updated.findIndex((i) => i.code === product.code && !isCartEnquiryLine(i));
            if (idx >= 0) {
                const { isUnlimited } = getCustomerStockConstraints(product);
                const remaining = getRemainingAddable(product, updated[idx].quantity);
                if (!isUnlimited && remaining < 1) {
                    window.alert(`Max stock reached for ${product.name || product.code}.`);
                } else {
                    updated[idx] = {
                        ...updated[idx],
                        ...product,
                        quantity: updated[idx].quantity + 1,
                        price: product.price != null ? product.price : updated[idx].price,
                        stock: product.stock !== undefined ? product.stock : updated[idx].stock,
                    };
                }
            }
            return updated;
        });
    };

    const decreaseQuantity = (product) => {
        if (isCartEnquiryLine(product)) {
            setCartItems((prev) =>
                prev.filter(
                    (i) =>
                        !(
                            isCartEnquiryLine(i) &&
                            String(i.code) === String(product.code)
                        )
                )
            );
            return;
        }
        setCartItems((prev) => {
            const updated = [...prev];
            const idx = updated.findIndex((i) => i.code === product.code && !isCartEnquiryLine(i));
            if (idx >= 0) {
                if (updated[idx].quantity > 1) {
                    updated[idx] = { ...updated[idx], quantity: updated[idx].quantity - 1 };
                } else {
                    updated.splice(idx, 1);
                }
            }
            return updated;
        });
    };

    const removeItem = (product) => {
        setCartItems((prev) =>
            prev.filter((i) => {
                if (isCartEnquiryLine(product)) {
                    return !(isCartEnquiryLine(i) && String(i.code) === String(product.code));
                }
                return !(String(i.code) === String(product.code) && !isCartEnquiryLine(i));
            })
        );
    };

    const clearCart = () => {
        setCartItems([]);
        setCartPriceNotice(null);
    };

    const totalAmount = calculateCartSubtotalGbp(cartItems);

    const fetchUserData = async (user, { force = false } = {}) => {
        const now = Date.now();
        if (
            !force &&
            userDataCache.current.data &&
            now - userDataCache.current.timestamp < 60000
        ) {
            return userDataCache.current.data;
        }
        try {
            const userDocRef = doc(db, 'users', user.uid);
            const snap = await getDoc(userDocRef);
            if (snap.exists()) {
                const data = snap.data();
                userDataCache.current = { data, timestamp: now };
                return data;
            }
        } catch (err) {
            console.error('Error fetching user data:', err);
        }
        return null;
    };

    const fetchUserAddresses = async (user) => {
        if (!user) return;
        const now = Date.now();
        if (addresses.length > 0 && now - addressesLastFetchedRef.current < 60000) {
            return;
        }
        const userData = await fetchUserData(user);
        if (!userData) return;
        const list = [];
        if (userData.billingAddress) {
            // Billing address always uses account companyName
            const billingAddr = { ...userData.billingAddress, companyName: userData.companyName || '' };
            list.push({ label: 'Billing Address', address: billingAddr });
        }
        if (userData.shippingAddress) {
            // Shipping address keeps its own companyName
            list.push({ label: 'Shipping Address', address: userData.shippingAddress });
        }
        Object.keys(userData).forEach((key) => {
            if (key.startsWith('saved_address_')) {
                const addr = userData[key];
                const label = addr.companyName || `${addr.firstName || ''} ${addr.lastName || ''}`.trim() || key;
                list.push({ label, address: addr });
            }
        });
        setAddresses(list);
        addressesLastFetchedRef.current = now;
    };

    const saveNewAddress = async (user, address) => {
        const uid = resolveUserDocIdForSavedAddress(user);
        if (!uid) return { ok: false, reason: 'no-user' };
        try {
            const userDocRef = doc(db, 'users', uid);
            const snap = await getDoc(userDocRef);
            const data = snap.exists() ? snap.data() : {};
            const fieldName = nextSavedAddressFieldName(data);
            const payload = buildSavedAddressPayload(address);
            await setDoc(userDocRef, { [fieldName]: payload }, { merge: true });
            return { ok: true, fieldName, address: payload, uid };
        } catch (err) {
            console.error('Error saving address:', err);
            return { ok: false, reason: err?.message || String(err) };
        }
    };

    // ===== PHASE B: STOCK RESERVATION (not immediate deduction) =====
    // Orders now RESERVE stock instead of immediately deducting it
    // Stock is only deducted when dispatch is CONFIRMED by user
    // This ensures: Current Stock = Last Stock Take + Production - Confirmed Dispatches
    const reserveStockForOrder = async (orderItems, userPricingKey, orderId, deliveryDate) => {
        const pricingKey = coerceCustomerPricingKey(userPricingKey);
        
        console.log(`📦 [CartContext] Reserving stock for order ${orderId} (${orderItems.length} items, delivery: ${deliveryDate})`);
        
        // CRITICAL: Expand any box products to their final components (handles nested boxes recursively)
        const itemsToReserve = orderItems.map(item => ({
            sku: item.code,
            quantity: item.quantity
        }));
        // For stock reservation, only reserve actual components (keepBoxes=false)
        const expandedItems = unifiedProductService.expandBoxes(itemsToReserve, 10, false);
        
        console.log(`📦 [CartContext] Expanded ${orderItems.length} order items to ${expandedItems.length} final products (boxes expanded)`);
        
        const reservations = [];
        const skipped = [];
        const errors = [];
        
        for (const item of expandedItems) {
            const code = item.sku;
            const quantity = item.quantity;
            
            if (!code) {
                console.warn('[CartContext] Skipping item without code:', item);
                skipped.push({ code: 'unknown', reason: 'No code' });
                continue;
            }
            
            try {
                // Check if unlimited stock (skip reservations)
                const product = await unifiedStockService.getProductStock(code, pricingKey, { useCache: false });
                
                if (!product) {
                    console.warn(`[CartContext] Product ${code} not found in products_${pricingKey}`);
                    skipped.push({ code, reason: 'Not found' });
                    continue;
                }
                
                if (product.Stock === 'x') {
                    console.log(`[CartContext] Skipping unlimited stock product: ${code}`);
                    skipped.push({ code, reason: 'Unlimited stock' });
                    continue;
                }
                
                // ===== RESERVE STOCK (don't deduct yet) =====
                const reservation = await unifiedStockService.reserveStock(code, quantity, {
                    orderId: orderId,
                    orderType: 'customer',
                    deliveryDate: deliveryDate,
                    reservedBy: 'customer',  // Will be enhanced with actual user email
                    reason: `Customer order ${orderId}`,
                    tier: pricingKey,
                    autoReleaseDate: new Date(new Date(deliveryDate).getTime() + 7 * 24 * 60 * 60 * 1000) // +7 days
                });
                
                console.log(`✅ [CartContext] Reserved ${quantity} units of ${code} (reservation: ${reservation.id})`);
                
                reservations.push({ 
                    code,
                    quantity,
                    reservationId: reservation.id,
                    reservedAt: new Date().toISOString()
                });
                
            } catch (error) {
                console.error(`❌ [CartContext] Reservation failed for ${code}:`, error);
                errors.push({ code, error: error.message });
                // Continue with other items
            }
        }
        
        const result = {
            success: errors.length === 0,
            reservations: reservations,
            skipped: skipped,
            errors: errors,
            totalReserved: reservations.length,
            totalSkipped: skipped.length,
            totalErrors: errors.length
        };
        
        console.log(`[CartContext] Stock reservation complete:`, {
            reserved: result.totalReserved,
            skipped: result.totalSkipped,
            errors: result.totalErrors,
            success: result.success
        });
        
        return result;
    };

    // ===== PHASE 5 MIGRATION: Unified stock update with two-phase commits =====
    // Replaces external Stock API calls with direct unifiedStockService
    // Now syncs BOTH warehouse and e-commerce automatically
    const updateStockInSheet = async (updates, userPricingKey = 'a', orderId = null) => {
        console.warn(
            '[CartContext] Stock updates are temporarily muted; skipping updateProductStockInFirestore.',
            {
                updatesCount: Array.isArray(updates) ? updates.length : 0,
                orderId,
                userPricingKey,
            }
        );

        // TODO: Re-enable unified stock updates once updateProductStockInFirestore is restored.
        return {
            success: true,
            muted: true,
            processed: 0,
            updates: Array.isArray(updates) ? updates : [],
        };
    };

    // Centralized Firestore write/upsert of the order (overwrites the earlier "reservation")
    const upsertOrderDoc = async (finalOrderData) => {
        try {
            await setDoc(doc(db, 'orders', finalOrderData.id), {
                ...finalOrderData,
                orderDate: Timestamp.fromDate(new Date()),
                lastUpdatedAt: serverTimestamp(),
                reserved: false, // reservation fulfilled
            }, { merge: true });
            setOrderStatus('Order saved to Firestore.');
        } catch (err) {
            console.error('Error saving order:', err);
            setOrderStatus('Failed to save order to Firestore.');
            throw err;
        }
    };

    // --------- NO CONFIRMATION EMAIL SENT FROM CONTEXT ---------
    // Returns a rich object so Cart.js can use shipping/customer/email if needed.
    const submitOrder = async (
        user,
        purchaseOrderInput,
        selectedAddress,
        isCollection = false,
        selectedBillingAddressObj = null,
        options = {}
    ) => {
        if (!user) {
            setOrderStatus('You must be logged in to place an order.');
            return;
        }
        const payInAdvance = options?.payInAdvance === true;
        console.log('🛒 [CartContext] submitOrder called with isCollection:', isCollection, 'payInAdvance:', payInAdvance);
        console.log('🛒 [CartContext] selectedBillingAddressObj:', selectedBillingAddressObj);
        const userData = await fetchUserData(user, { force: true });
        if (!userData || !userData.companyName) {
            setOrderStatus('Invalid user account.');
            return;
        }

        const normalized = isCollection ? null : validateAndNormalizeAddress(selectedAddress);
        if (!isCollection && !normalized) {
            setOrderStatus('Invalid delivery address.');
            return;
        }

        const name = getUserName(userData);
        const shippingBlock = isCollection
            ? toShippingBlockForCollection(userData, name)
            : toShippingBlockFromNormalized(normalized, userData, name);

        const pricingKey = resolvePricingKeyFromUserData(userData);

        const { orderable: orderableCartItems } = partitionCartItems(cartItems);
        if (!orderableCartItems.length) {
            const message =
                'Add at least one product to place an order. Enquiry reminders cannot be submitted alone.';
            setOrderStatus(message);
            return { ok: false, message };
        }

        const revalidation = await revalidateCartAgainstTier(cartItems, pricingKey);
        if (!revalidation.ok) {
            const message =
                revalidation.message || 'Some items are not on your price list. Please review your cart.';
            setOrderStatus(message);
            return { ok: false, message };
        }

        const validatedLines = filterOrderableCartItems(revalidation.lines);
        if (!validatedLines.length) {
            const message =
                'Add at least one product to place an order. Enquiry reminders cannot be submitted alone.';
            setOrderStatus(message);
            return { ok: false, message };
        }
        const actionableCorrections = filterActionablePriceCorrections(
            revalidation.priceCorrections,
            orderableCartItems,
            pricingKey
        );

        if (revalidation.priceCorrections?.length) {
            setCartItems(revalidation.lines);
        }

        if (actionableCorrections.length) {
            const notice = buildCartPriceChangeNotice(actionableCorrections, orderableCartItems);
            setCartPriceNotice(notice);
            const detail = notice?.details ? `\n\n${notice.details}` : '';
            const message =
                (notice?.message ||
                    'Prices on your price list have changed since these items were added to your cart.') +
                detail +
                '\n\nPlease review your updated cart and submit again.';
            setOrderStatus(message);
            return { ok: false, priceCorrections: actionableCorrections, message };
        }

        const validatedOrderItems = validatedLines.map((item, index) =>
            buildOrderDeskLineFromCartLine(item, index)
        );

        const {
            subtotalGbp,
            shippingCharge: shippingTotal,
            shippingReason,
            orderTotalGbp,
        } = calculateWholesaleShippingFromCart({
            lineItems: validatedLines,
            isCollection,
            pricingKey,
        });

        console.log('[CartContext] Shipping at submit:', {
            subtotalGbp,
            shippingTotal,
            shippingReason,
            orderTotalGbp,
            pricingKey,
            isCollection,
        });

        const formattedDelivery = formatDDMMYYYY(deliveryDate);

        // 1) Get a UNIQUE order ID (try the provided PO first; fallback to auto)
        const preferred = (purchaseOrderInput && purchaseOrderInput.trim() !== '') ? purchaseOrderInput.trim() : null;
        let finalOrderId;
        try {
            finalOrderId = await generateUniqueOrderId(preferred);
        } catch (e) {
            console.error('[CartContext] Failed to reserve an order ID:', e?.message || e);
            setOrderStatus('Could not reserve an order number. Please try again.');
            return;
        }

        // 2) Build the order payload
        // OrderDesk Field Mapping:
        // - shipping_method: userData.poSystem (priority) || 'Bermondsey' (collection) || userData.Method || 'Cargo Bike'
        // - customer_id: userData.companyName
        // - pricingKey: userData.Pricing
        const shippingMethod = userData.poSystem || (isCollection ? 'Bermondsey' : (userData.Method || 'Cargo Bike'));
        
        console.log('🛒 [CartContext] User data loaded:', {
            companyName: userData.companyName,
            poSystem: userData.poSystem,
            Method: userData.Method,
            Pricing: pricingKey,
            isCollection
        });
        console.log('🛒 [CartContext] Calculated shipping_method:', shippingMethod);
        console.log('🛒 [CartContext] Billing address will be:', selectedBillingAddressObj?.address ? 'Present' : 'Missing');
        
        const additionalEmailCopies = extractAdditionalEmailRowsFromUserProfileData(userData);

        let finalOrderData = applySearcysOrderFields({
            accountCode: userData.username,
            pricingKey,
            companyName: userData.companyName,
            baseOrderId: finalOrderId,
            customXeroId: userData.customXeroId,
            order: {
                DOCMatch_id: user.uid,
                id: finalOrderId,
                source_id: finalOrderId,
                customer_id: userData.companyName,
                email: user.email,
                delivery_date: deliveryDate.toISOString(),
                shipping_method: shippingMethod,
                shipping_total: shippingTotal,
                handling_total: 0,
                tax_total: 0,
                order_total: orderTotalGbp.toFixed(2),
                folder_id: 240791,
                date_added: new Date().toISOString(),
                date_updated: new Date().toISOString(),
                status: 'Pending',
                customer: {
                    first_name: name.first,
                    last_name: name.last,
                    phone: resolveUserPhone(userData),
                    company: userData.companyName,
                    email: user.email,
                },
                billing: selectedBillingAddressObj?.address ? {
                    first_name: name.first,
                    last_name: name.last,
                    company: selectedBillingAddressObj.address.companyName || userData.companyName,
                    address1: selectedBillingAddressObj.address.line1 || '',
                    address2: selectedBillingAddressObj.address.line2 || '',
                    address3: selectedBillingAddressObj.address.line3 || '',
                    city: selectedBillingAddressObj.address.city || '',
                    state: '',
                    postal_code: selectedBillingAddressObj.address.postCode || selectedBillingAddressObj.address.postalCode || '',
                    country: 'GB',
                    phone: selectedBillingAddressObj.address.phone || resolveUserPhone(userData),
                } : null,
                shipping: shippingBlock,
                order_items: validatedOrderItems,
                Notes: [
                    {
                        date_added: new Date().toISOString(),
                        username: user.email || 'Unknown',
                        content: orderNote || 'No additional notes.',
                    },
                ],
                checkout_data: {
                    Delivery: formattedDelivery,
                    Notes: orderNote || '',
                },
                collection: !!isCollection,
                subtotal: subtotalGbp,
                shippingReason: shippingReason || null,
                pricingKey,
                createdAt: serverTimestamp(),
                lastUpdatedAt: serverTimestamp(),
                reserved: false, // will be explicitly written below
                ...(additionalEmailCopies.length > 0
                    ? { accountAdditionalEmailCopies: additionalEmailCopies }
                    : {}),
            },
        });

        if (payInAdvance) {
            finalOrderData = applyPrepaidMarkersToOrder(finalOrderData);
        }

        // 3) Ensure the order doc is written BEFORE external calls
        //    (this overwrites the earlier reservation document)
        try {
            await upsertOrderDoc(finalOrderData);
        } catch (e) {
            // If we fail to write the order, don't continue to external systems
            return;
        }

        // 4) Send to OrderDesk (best-effort). If it fails, we still keep the order in Firestore.
        let orderDeskFailed = false;
        let orderDeskError = null;
        try {
            const networkConditions = getNetworkConditions();
            const startTime = performance.now();
            
            await sendOrderToOrderDesk(prepareOrderPayloadForOrderDesk(finalOrderData));
            
            const duration = performance.now() - startTime;
            await logAPICall('OrderDesk API', 'POST', 200, duration, {
                orderId: finalOrderData.id,
                networkConditions
            });
            
            // Mark status -> "Pending" (customer-facing status)
            await updateDoc(doc(db, 'orders', finalOrderId), {
                status: 'Pending',
                orderDeskStatus: 'success',
                date_updated: new Date().toISOString(),
                lastUpdatedAt: serverTimestamp(),
            });
        } catch (e) {
            const duration = performance.now() - startTime;
            await logAPICall('OrderDesk API', 'POST', 0, duration, {
                error: e.message,
                networkConditions: getNetworkConditions()
            });
            
            await logError(e, {
                context: 'orderdesk_submission',
                orderId: finalOrderData.id
            });
            
            console.error('OrderDesk submission failed:', e?.message || e);
            orderDeskFailed = true;
            orderDeskError = e?.message || String(e);
            
            // Update order with OD failure flags but show as "Pending" to customer
            await updateDoc(doc(db, 'orders', finalOrderId), {
                status: 'Pending', // Customer sees "Pending"
                orderDeskStatus: 'failed', // Internal flag
                orderDeskError: orderDeskError,
                orderDeskFailedAt: new Date().toISOString(),
                requiresManualSubmission: true,
                date_updated: new Date().toISOString(),
                lastUpdatedAt: serverTimestamp(),
            });

            // Capture for admin recovery (push = submit the already-saved order to OrderDesk).
            // Customer should NOT see any indication; this is purely for internal ops.
            try {
                await captureFailedOrder({
                    cartItems: validatedLines,
                    deliveryDate,
                    isCollection,
                    purchaseOrder: preferred || finalOrderId,
                    orderNote,
                    userId: user?.uid,
                    userEmail: user?.email,
                    companyName: userData?.companyName || user?.displayName || '',
                    userData,
                    selectedAddress: shippingBlock,
                    selectedBillingAddress: selectedBillingAddressObj || null,
                    subtotal: subtotalGbp,
                    shipping: shippingTotal,
                    total: orderTotalGbp,
                }, e, {
                    failurePoint: 'orderdesk_submission',
                    attemptedOrderId: finalOrderId,
                });
            } catch (capErr) {
                console.warn('[CartContext] Failed to capture failed order for OrderDesk failure:', capErr);
            }
            
            // Send critical failure email to admin
            try {
                const itemsList = validatedOrderItems
                    .map((item) => `  - ${item.name} (${item.code}) x${item.quantity} @ £${item.price}`)
                    .join('\n');
                const failureEmailPayload = {
                    to: 'orders@londonsmokeandcure.co.uk',
                    subject: `🚨 URGENT: OrderDesk Submission Failed - Order #${finalOrderId}`,
                    html: `
                        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                            <div style="background: #dc2626; color: white; padding: 20px; border-radius: 8px 8px 0 0;">
                                <h1 style="margin: 0; font-size: 24px;">🚨 OrderDesk Submission Failed</h1>
                            </div>
                            <div style="background: #f9fafb; padding: 20px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 8px 8px;">
                                <h2 style="color: #dc2626; margin-top: 0;">Action Required</h2>
                                <p><strong>An order was successfully saved in the system but failed to submit to OrderDesk.</strong></p>
                                <p>You must manually place this order on behalf of the customer.</p>
                                
                                <div style="background: white; padding: 15px; border-radius: 6px; margin: 20px 0;">
                                    <h3 style="margin-top: 0; color: #1f2937;">Order Details</h3>
                                    <p><strong>Order Number:</strong> ${finalOrderId}</p>
                                    <p><strong>Customer:</strong> ${userData.companyName}</p>
                                    <p><strong>Contact:</strong> ${name.first} ${name.last}</p>
                                    <p><strong>Email:</strong> ${user.email}</p>
                                    <p><strong>Phone:</strong> ${resolveUserPhone(userData) || 'N/A'}</p>
                                    <p><strong>Delivery Date:</strong> ${formattedDelivery}</p>
                                    <p><strong>Shipping Method:</strong> ${shippingMethod}</p>
                                    <p><strong>Order Total:</strong> £${orderTotalGbp.toFixed(2)}</p>
                                </div>
                                
                                <div style="background: white; padding: 15px; border-radius: 6px; margin: 20px 0;">
                                    <h3 style="margin-top: 0; color: #1f2937;">Delivery Address</h3>
                                    <p style="margin: 5px 0;">${shippingBlock.company || ''}</p>
                                    <p style="margin: 5px 0;">${shippingBlock.address1}</p>
                                    ${shippingBlock.address2 ? `<p style="margin: 5px 0;">${shippingBlock.address2}</p>` : ''}
                                    ${shippingBlock.address3 ? `<p style="margin: 5px 0;">${shippingBlock.address3}</p>` : ''}
                                    <p style="margin: 5px 0;">${shippingBlock.city}, ${shippingBlock.postal_code}</p>
                                </div>
                                
                                <div style="background: white; padding: 15px; border-radius: 6px; margin: 20px 0;">
                                    <h3 style="margin-top: 0; color: #1f2937;">Order Items</h3>
                                    <pre style="background: #f3f4f6; padding: 10px; border-radius: 4px; overflow-x: auto;">${itemsList}</pre>
                                </div>
                                
                                <div style="background: #fef2f2; border: 2px solid #dc2626; padding: 15px; border-radius: 6px; margin: 20px 0;">
                                    <h3 style="margin-top: 0; color: #dc2626;">Error Details</h3>
                                    <p style="font-family: monospace; font-size: 12px; color: #7f1d1d;">${orderDeskError}</p>
                                </div>
                                
                                <div style="background: #dbeafe; border-left: 4px solid #3b82f6; padding: 15px; margin: 20px 0;">
                                    <h3 style="margin-top: 0; color: #1e40af;">Next Steps</h3>
                                    <ol style="margin: 10px 0; padding-left: 20px;">
                                        <li>Review the order details above</li>
                                        <li>Manually submit the order to OrderDesk</li>
                                        <li>Update the order status in the Admin Dashboard</li>
                                        <li>Send confirmation email to customer</li>
                                        <li>Click "Resend to OrderDesk" in Admin Dashboard to retry submission</li>
                                    </ol>
                                </div>
                                
                                <p style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 12px;">
                                    <strong>Note:</strong> The customer sees this order as "Pending" and has no indication of the failure. 
                                    Handle this order promptly to maintain service quality.
                                </p>
                            </div>
                        </div>
                    `
                };
                
                // Send via transporter API
                const transporterUrl = 'https://newsletter-transporter-service-844411985132.europe-west2.run.app/send-alert';
                await fetch(transporterUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(failureEmailPayload)
                });
                
                console.log('✅ [CartContext] Alert email sent to orders@londonsmokeandcure.co.uk');
            } catch (emailError) {
                console.error('❌ [CartContext] Failed to send alert email:', emailError);
            }
            
            // Continue — we still proceed to stock update to reflect local intent
        }

        // 5) Reserve stock (PHASE B: Don't deduct immediately, wait for dispatch confirmation)
        try {
            const reservationResult = await reserveStockForOrder(
                validatedOrderItems,
                pricingKey,
                finalOrderId,
                finalOrderData.deliveryDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // Default +7 days
            );
            
            // Build reservation map: { sku: reservationId } — omit undefined IDs (Firestore rejects them)
            const stockReservations = {};
            reservationResult.reservations.forEach((res) => {
                if (res.reservationId != null && res.reservationId !== '') {
                    stockReservations[res.code] = res.reservationId;
                }
            });
            
            await updateDoc(doc(db, 'orders', finalOrderId), {
                // Stock reservation fields (PHASE B)
                stockReservations: stockReservations,
                stockReserved: reservationResult.totalReserved > 0,
                stockReservationDetails: {
                    reserved: reservationResult.totalReserved,
                    skipped: reservationResult.totalSkipped,
                    errors: reservationResult.totalErrors,
                    timestamp: new Date().toISOString()
                },
                
                // Dispatch status fields (PHASE B)
                dispatchStatus: 'pending',  // pending | confirmed | cancelled
                dispatchScheduledDate: finalOrderData.deliveryDate || Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
                
                // Legacy compatibility
                stockSync: reservationResult.success ? 'RESERVED' : 'PARTIAL',
                lastUpdatedAt: serverTimestamp(),
            });
            
            console.log(`[CartContext] Stock reserved for order ${finalOrderId}:`, {
                reserved: reservationResult.totalReserved,
                skipped: reservationResult.totalSkipped,
                errors: reservationResult.totalErrors
            });
        } catch (e) {
            console.warn('Stock reservation failed, order still submitted.');
            await updateDoc(doc(db, 'orders', finalOrderId), {
                stockSync: 'ERROR',
                stockSyncError: e?.message || String(e),
                dispatchStatus: 'pending',  // Still create order even if reservation fails
                lastUpdatedAt: serverTimestamp(),
            });
        }

        // 6) Clear cart locally
        setCartItems([]);
        setOrderStatus('Order submitted successfully!');

        // 7) Return a richer payload for Cart.js
        return {
            ok: true,
            id: finalOrderData.id,
            email: finalOrderData.email,
            customer: finalOrderData.customer,
            shipping: finalOrderData.shipping,
            order_items: finalOrderData.order_items,
            folder_id: finalOrderData.folder_id,
            orderDeskFailed,
            orderDeskError,
        };
    };

    return (
        <CustomerPricingKeyContext.Provider value={customerPricingKey}>
            <CartContext.Provider
                value={{
                    cartItems,
                    orderItems,
                    addToCart,
                    addItemsToCart,
                    addEnquiryReminder,
                    increaseQuantity,
                    decreaseQuantity,
                    removeItem,
                    clearCart,
                    totalAmount,
                    submitOrder,
                    saveNewAddress,
                    setOrderNote,
                    deliveryDate,
                    setDeliveryDate,
                    setPurchaseOrder,
                    orderStatus,
                    customerPricingKey,
                    cartPriceNotice,
                    revalidateCartPrices,
                    dismissCartPriceNotice,
                    fetchUserAddresses,
                    addresses,
                    updateStockInSheet,
                }}
            >
                {children}
            </CartContext.Provider>
        </CustomerPricingKeyContext.Provider>
    );
}

export default CartProvider;
